import queryString from 'query-string';
import makeRequest from './makeRequest';
import {
  FETCH_MAIN_FILTER,
  FETCH_SECONDARY_FILTER,
  FETCH_OFFERS,
  FETCH_DESTINATIONS,
  MARKET,
  LOCALE,
  FETCH_PRICE_GRAPH,
  CURRENCY,
  FETCH_SESSION,
  FETCH_AUTOCOMPLETE_CITY,
  FETCH_STATIC_HOTELS,
  FETCH_FAVORITE_LIST,
  SET_ONBOARDING_EMAIL,
  FETCH_HOTEL,
  FETCH_USER_PROFILE,
  FETCH_SEARCH_PERFORMED,
} from '../constants';
import { DEALS_PER_PAGE } from '../constants/constants';
import { pushEvent } from '../utilities/analytics';
import { LOAD_SEARCH_RESULTS_EVENT, USER_PROFILE_SOFT_AUTHENTICATED } from '../constants/analyticEvents';
import { FETCH_USER_ID } from '../constants';
import { getCookie } from '../utilities/helpers';
import abTesting from '../services/abTesting';

const env = 'production';

const fetchMainFilters = async function () {
  if (env === 'development') {
    return fetch('./server-responses/mainFilter.json', { method: 'GET' })
      .then((response) => response.json())
      .then((json) => {
        return json;
      });
  }

  return makeRequest({
    url:
      FETCH_MAIN_FILTER +
      `?data=${JSON.stringify({ locale: LOCALE, market: MARKET })}`,
  }).then((res) => res.data);
};

export async function fetchSecondaryFilter() {
  if (env === 'development') {
    // return require('./server-responses/secondaryFilter.json');
    return fetch('./server-responses/secondaryFilter.json', { method: 'GET' })
      .then((response) => response.json())
      .then((json) => {
        return json;
      });
  }

  return makeRequest({
    url:
      FETCH_SECONDARY_FILTER +
      `?data=${JSON.stringify({ locale: LOCALE, market: MARKET })}`,
    method: 'GET',
  }).then((res) => res.data);
}

export async function fetchDealsList(filters, loadAllDeals, userProfileData, signal, startTime, requestType) {
  const filtersToLocalStorage = {...filters, date: Date.now() }
  // TODO: Should be placed in different place
  // Save filters to use it on the hfOffer page
  localStorage.setItem('hfLastFilters', JSON.stringify(filtersToLocalStorage));
  sessionStorage.setItem('hfLastFilters', JSON.stringify(filtersToLocalStorage)); // it's also needed for analytics

  let paramsObject = {
    ...filters,
    limit: loadAllDeals ? 100 : DEALS_PER_PAGE,
    sortDisplayVersion: window.holidayFinder?.sortDisplayVersion,
  };

  if (!loadAllDeals && abTesting.dealOfTheDay && filters.offset === 0) {
    // if deal of the day is turned on -> load 19 offers for the first page
    paramsObject.limit = DEALS_PER_PAGE - 1;
  }
  if (!loadAllDeals && abTesting.dealOfTheDay && filters.offset > 0) {
    // if deal of the day is turned on -> also set (offset - 1) for the pages > 1
    paramsObject.offset = filters.offset - 1;
  }

  const profileId = userProfileData?.searchProfileData?.id || 0;

  if (profileId || profileId === 0) {
    paramsObject = {
      ...paramsObject,
      searchUserProfile: profileId,
    };
  }

  let searchParams = `?data=${JSON.stringify(paramsObject)}`

  if (getCookie('hf_show_hidden_gems')) {
    searchParams += '&hf_show_hidden_gems=true';
  }

  // if (getCookie('hfdebug') || abTesting.userPrefEnabled) {
    const userData = JSON.parse(localStorage.getItem('hf_user'));
    if (userData) {
      searchParams += `&muid=${userData?.user?.muid}`;
    }
  // }

  return makeRequest({
    method: 'GET',
    url: FETCH_OFFERS + searchParams,
    signal,
  }).then((response) => {
    const loadDurationTime = (Date.now() - startTime)/1000; // load offer request duration time (in seconds) for analytics
    pushEvent(LOAD_SEARCH_RESULTS_EVENT, { loadDurationTime, requestType, responseData: response.data, userProfileData });
    if (!response.data.success) throw new Error('Data fetch error');

    return response.data.data;
  });
}

export async function fetchDealOfTheDay(filters, signal) {
  // fetch deal of the day
  const paramsObject = {
    ...filters,
    limit: 20,
    sortDisplayVersion: window.holidayFinder?.sortDisplayVersion,
    dealOfTheDay: true,
  };

  let searchParams = `?dealoftheday=true&data=${JSON.stringify(paramsObject)}`;

  const userData = JSON.parse(localStorage.getItem('hf_user'));
  if (userData) {
    searchParams += `&muid=${userData?.user?.muid}`;
  }

  return makeRequest({
    method: 'GET',
    url: FETCH_OFFERS + searchParams,
    signal,
  }).then((response) => {
    if (!response.data.success) throw new Error('Deal of the day fetch error');

    return response.data.data;
  });
}

const getUserProfile = async function (filters, style, signal) {
  const requestObject = {
    ...filters,
    searchProfileVersion: window.holidayFinder.searchProfileVersion
      ? window.holidayFinder.searchProfileVersion
      : 1,
  };

  const hostingLevelLS = localStorage.getItem('hostingLevel')
    ? +localStorage.getItem('hostingLevel')
    : undefined;

  if (style || style === 0) {
    requestObject.hostingLevel = style + 1;
    localStorage.setItem('hostingLevel', style);
  } else if (hostingLevelLS || hostingLevelLS === 0) {
    requestObject.hostingLevel = hostingLevelLS + 1;
  }

  return makeRequest({
    method: 'GET',
    url: FETCH_USER_PROFILE + `?data=${JSON.stringify(requestObject)}`,
    signal,
  }).then((response) => {
    //if (response.data.errorCode !== 0) throw new Error('Data fetch error');
    return response;
  }).catch((error) => {
    console.log('Get user profile request failed', error);
  });
};

export async function fetchLocationDealsList(params) {
  if (env === 'development') {
    return fetch('./server-responses/hotelsDeals.json', { method: 'GET' })
      .then((response) => response.json())
      .then((json) => json.data);
  }

  return makeRequest({
    method: 'GET',
    url:
      FETCH_STATIC_HOTELS +
      `?data=${JSON.stringify({
        locale: LOCALE,
        ...params,
      })}`,
  }).then((res) => res.data.data);
}

const fetchDestinationsRequest = async function (data) {
  // TODO: Add params
  return makeRequest({
    url: FETCH_DESTINATIONS,
  }).then((res) => res.data);
};

export async function fetchPriceGraphRequest(params) {
  if (env === 'development') {
    return fetch('./server-responses/priceGraph.json', { method: 'GET' })
      .then((response) => response.json())
      .then((json) => json.data);
  }

  return makeRequest({
    url:
      FETCH_PRICE_GRAPH +
      `?data=${JSON.stringify({
        currency: CURRENCY,
        ...params,
      })}`,
    method: 'get',
  }).then((res) => res.data.data);
}

export async function fetchSessionRequest(params) {
  return makeRequest({
    url: FETCH_SESSION + `?data=${JSON.stringify({ ...params })}`,
  }).then((res) => res.data);
}

export async function fetchLocationDealsPrices(params, isPricesFetched) {
  const { onTheFlySessionId, priorityHotels, ...otherParams } = params;
  const currentTimestamp = Date.now();
  let url = `${FETCH_OFFERS}?onTheFlySessionId=${onTheFlySessionId}&data=${JSON.stringify(
    otherParams
  )}&tt=${currentTimestamp}`;

  // if (!isPricesFetched) {
    url = `${url}&priorityHotels=[${priorityHotels}]`;
  // }

  const userData = JSON.parse(localStorage.getItem('hf_user'));
  if (userData) {
    url += `&muid=${userData?.user?.muid}`;
  }

  return makeRequest({
    url,
  }).then((res) => res.data);
}

export async function getShortUrl(longUrl) {
  return makeRequest({
    url: `http://18.185.10.112/l/put`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body: `url=${encodeURIComponent(longUrl)}`,
  }).then((res) => res.data);
}

export async function getAutocompleteCities(str) {
  return makeRequest({
    url: `${FETCH_AUTOCOMPLETE_CITY}${str}`,
  }).then((res) => res.data);
}

const fetchFavoriteList = async function (favoritesList) {
  // if (env === "development") {
  //   return require("./server-responses/secondaryFilter.json");
  // }

  const userData = JSON.parse(localStorage.getItem('hf_user'));

  return makeRequest({
    url:
      FETCH_FAVORITE_LIST +
      `?data=${JSON.stringify({
        locale: LOCALE,
        currency: CURRENCY,
        market: MARKET,
        hfOffers: favoritesList,
      })}&muid=${userData?.user?.muid}`,
    method: 'POST',
  }).then((res) => res.data);
};

export async function setOnboardingEmail(props) {
  return makeRequest({
    url: `${SET_ONBOARDING_EMAIL}`,
    method: 'POST',
    body: props,
  });
}

const fetchHotelsByName = async function (name, destinationIds) {
  let url = `${FETCH_HOTEL}?data=${name}&locale=${LOCALE}&marketId=${window.dataGlobalSettings?.marketId}`;
  if (destinationIds) url += `&destinations=${destinationIds.join(',')}`;
  return makeRequest({ url });
};

const fetchHotelById = async function (id, destinationIds) {
  let url = `${FETCH_HOTEL}?hotelId=${id}&locale=${LOCALE}&marketId=${window.dataGlobalSettings?.marketId}`;
  if (destinationIds) {
    url += `&destinations=${
      typeof destinationIds === 'number'
        ? destinationIds
        : destinationIds.join(',')
    }`;
  }
  return makeRequest({ url });
};

const sendSearchStatistic = async function (params) {
  return makeRequest({
    url: `/ajax/hf.php?${queryString.stringify(params, {
      arrayFormat: 'comma',
    })}`,
  });
};

const getUserById = async function (data) {
  const response = await makeRequest({
    method: 'POST',
    url: FETCH_USER_ID,
    body: data,
  })

  if (response.data?.muid) {
    localStorage.setItem('user', JSON.stringify(response.data));
    pushEvent(USER_PROFILE_SOFT_AUTHENTICATED, { muid: response.data.muid, data: response.data });
  }
};

const updateUserById = async function (data) {
  const response = await makeRequest({
    method: 'POST',
    url: window.apiV6Config.path+ `/user/context?muid=${data.user.muid}&providedEmail=wizard`,
    body: data
  })

  if (response.data?.user) {
    localStorage.setItem('hf_user', JSON.stringify(response.data));
    window.dispatchEvent(new Event('userContextSet')); // not sure if this event should be here
  }
};

const updateContextWithDeparture = async function(data) {
  // send departure airport on its change
  if (data && data?.user?.muid && data?.user?.originAirports) {
    const response = await makeRequest({
      method: 'POST',
      url: window.apiV6Config.path+ `/user/context?muid=${data.user.muid}`,
      body: data
    });
    if (response.data?.user) {
      console.log('Context updated:', response.data);
    }
  }
};

const sendSearchPerformed = async function (data) {
  const response = await makeRequest({
    method: 'POST',
    url: FETCH_SEARCH_PERFORMED,
    body: data,
  });
  console.log('sendSearchPerformed:', response);
};

const API = {
  fetchFavoriteList,
  fetchMainFilters,
  fetchDestinationsRequest,
  fetchHotelsByName,
  fetchHotelById,
  fetchLocationDealsList,
  sendSearchStatistic,
  getUserProfile,
  getUserById,
  sendSearchPerformed,
  updateUserById,
  updateContextWithDeparture,
};

export default API;
